import {AnimatePresence, motion} from 'framer-motion';
import {Button, closeCurrentDialog, DataStatus} from 'platform/components';
import {
  Box,
  Center,
  getSize,
  Heading,
  HStack,
  Icon,
  Image,
  Right,
  Space,
  Text,
  VStack,
} from 'platform/foundation';
import {useDateTimeFormatter} from 'platform/locale';
import styled from 'styled-components';

import {useState} from 'react';

import {isNilOrEmpty} from 'ramda-adjunct';

import {TemplateContextEnum} from '@dms/api/core';
import i18n from '@dms/i18n';
import {useRenderTemplateExample} from '@dms/settings/shared';
import {handleApiError} from '@dms/shared';

import {DOT_CHARACTER, suffixTestId, TestIdProps, useBoolean, useOnMount} from 'shared/web';

import imageUrl from '../assets/favicon.png';

interface SmsMessagePreviewProps extends TestIdProps {
  body: string;
  context: TemplateContextEnum;
}

const variants = {
  hidden: {
    opacity: 0,
    scaleY: 0,
    originY: 0,
  },
  visible: {
    opacity: 1,
    scaleY: 1,
    originY: 0,
  },
};

export const SmsMessagePreview = (props: SmsMessagePreviewProps) => {
  const formatDateTime = useDateTimeFormatter();
  const {renderSms, isLoading, isError} = useRenderTemplateExample(props.context);

  const [renderedBody, setRenderedBody] = useState<string | null>(null);

  const [wasFirstMessageLoaded, loadFirstMessage] = useBoolean();

  const render = () => {
    renderSms?.({
      body: {template: props.body},
    })
      .unwrap()
      .then((res) => setRenderedBody(res.body))
      .then(loadFirstMessage)
      .catch(handleApiError);
  };

  useOnMount(render);

  const messageSentAtLabel = `SMS ${DOT_CHARACTER} ${i18n.t('general.labels.today')}, ${formatDateTime('timeShort', new Date())}`;
  const customerName = 'Omnetic';

  return (
    <VStack spacing={4}>
      <Box
        minHeight={100}
        width="100%"
        height="100%"
        backgroundColor="palettes.neutral.20.100"
        borderRadius="large"
      >
        <Center height="100%">
          <VStack height="100%" justify="flex-end">
            <Space vertical={4} />

            <Heading size={4} align="center" data-testid={suffixTestId('smsMessagePreview', props)}>
              {i18n.t('general.labels.smsPreview')}
            </Heading>

            <Space vertical={8} />

            <PhonePreview>
              <DataStatus
                minHeight="100%"
                isLoading={isLoading}
                isError={isError}
                isEmpty={isNilOrEmpty(renderedBody)}
              >
                <HStack spacing={2}>
                  <Box flex={1}>
                    <AnimatePresence mode="wait">
                      {/* eslint-disable-next-line eag/prefer-show -- Pre-existing violation */}
                      {wasFirstMessageLoaded && (
                        <motion.div
                          key="sms-message"
                          variants={variants}
                          initial="hidden"
                          animate="visible"
                          exit="hidden"
                          transition={{duration: 0.3, ease: 'easeInOut'}}
                        >
                          <Space vertical={1} />
                          <VStack spacing={1}>
                            <MessageBox>
                              <VStack spacing={1} maxHeight={100}>
                                <HStack justify="space-between">
                                  <Icon value="action/settings" isNotFilled size={4} />
                                  <Heading
                                    size={5}
                                    color="white"
                                    data-testid={suffixTestId('customerName', props)}
                                  >
                                    {customerName}
                                  </Heading>
                                </HStack>

                                <Box overflow="hidden" position="relative">
                                  <Text
                                    size="small"
                                    color="white"
                                    data-testid={suffixTestId('messageBody', props)}
                                    overflowWrap="anywhere"
                                    preWrap
                                  >
                                    {renderedBody}
                                  </Text>
                                </Box>
                              </VStack>
                            </MessageBox>

                            <Text
                              size="xxSmall"
                              color="secondary"
                              data-testid={suffixTestId('messageSentAtLabel', props)}
                            >
                              {messageSentAtLabel}
                            </Text>
                          </VStack>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </Box>

                  <Box
                    width={7}
                    height={7}
                    backgroundColor="palettes.neutral.20.100"
                    borderRadius="circular"
                  >
                    <Center width="100%" height="100%">
                      <Image
                        width={5}
                        height={5}
                        src={imageUrl}
                        borderRadius="circular"
                        data-testid={suffixTestId('phoneIcon', props)}
                      />
                    </Center>
                  </Box>
                </HStack>
              </DataStatus>
            </PhonePreview>
          </VStack>
        </Center>
      </Box>
      <Right>
        <Button
          title={i18n.t('general.actions.close')}
          onClick={closeCurrentDialog}
          variant="secondary"
        />
      </Right>
    </VStack>
  );
};

const PREVIEW_BOX_WIDTH = 95;
const PREVIEW_BOX_MIN_HEIGHT = 100;

// this custom pixel-perfect design taken from graphic team figma
// eslint-disable-next-line eag/no-css-property
const PhonePreview = styled.div`
  width: ${getSize(PREVIEW_BOX_WIDTH)};
  background-color: ${({theme}) => theme.colors.general.white};
  border: 10px solid ${({theme}) => theme.colors.palettes.neutral[800][100]};
  border-top-left-radius: 48px;
  border-top-right-radius: 48px;
  border-bottom: none;
  padding: ${getSize(7)};
  min-height: ${getSize(PREVIEW_BOX_MIN_HEIGHT)};
`;

const MessageBox = styled.div`
  padding: ${getSize(2)};
  background-color: ${({theme}) => theme.colors.palettes.neutral[800][100]};
  border-bottom-left-radius: ${({theme}) => theme.radii.medium};
  border-bottom-right-radius: ${({theme}) => theme.radii.medium};
  border-top-left-radius: ${({theme}) => theme.radii.medium};
  position: relative;
`;
